/**
 * ProjectDetailsModal — edit ALL of a project's details from the estimate
 * detail page and persist them (B-122). Uses the shared ProjectFieldsFieldset
 * so it stays in lock-step with the create form (B-123).
 */
import { useState } from 'react';

import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { ProjectFieldsFieldset, ProjectFieldValues } from './ProjectFieldsFieldset';
import { BackendAPI } from '../../lib/api';
import type { Project } from '../../types';

function toValues(p: Project): ProjectFieldValues {
  return {
    name: p.name ?? '',
    owner_name: p.owner_name ?? '',
    owner_phone: p.owner_phone ?? '',
    owner_email: p.owner_email ?? '',
    street: p.street ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    zip: p.zip ?? '',
    claim_number: p.claim_number ?? '',
    policy_number: p.policy_number ?? '',
    date_of_loss: (p.date_of_loss ?? '').slice(0, 10),
    date_of_inspection: (p.date_of_inspection ?? '').slice(0, 10),
    loss_type: p.loss_type ?? '',
  };
}

export function ProjectDetailsModal({
  project,
  onClose,
  onSaved,
}: {
  project: Project;
  onClose: () => void;
  onSaved: (updated: Project) => void;
}) {
  const [values, setValues] = useState<ProjectFieldValues>(() => toValues(project));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onChange = (patch: Partial<ProjectFieldValues>) =>
    setValues((v) => ({ ...v, ...patch }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // On web the route id IS the server id, so updateProject takes it directly.
      const updated = await BackendAPI.updateProject(project.id, values as Partial<Project>);
      onSaved(updated);
    } catch (e: any) {
      setError(e?.message || 'Failed to save changes.');
      setSaving(false);
    }
  };

  return (
    <Modal
      label="Edit project details"
      title="Project details"
      subtitle="Edits are saved to the project."
      onClose={onClose}
      testId="project-details-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={saving} data-testid="project-details-save">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3 rounded-md border border-[var(--danger)] bg-[var(--danger-bg,rgba(220,50,50,.08))] px-3 py-2 text-[12.5px] text-[var(--danger)]">
          {error}
        </div>
      )}
      {/* Property type is immutable after creation (PRP-ROOF-2 S0) — shown
          read-only, with no editing affordance. */}
      <div
        className="mb-3 flex flex-col gap-1.5 text-[11px] font-[550] uppercase tracking-[.02em] text-[var(--tx-lo)]"
        data-testid="project-type-readonly"
      >
        Property type
        <span className="text-[13px] font-[450] normal-case tracking-normal text-[var(--tx-hi)]">
          {project.project_type === 'commercial' ? 'Commercial' : 'Residential'}
        </span>
      </div>
      <ProjectFieldsFieldset value={values} onChange={onChange} />
    </Modal>
  );
}
